import React, { useState } from 'react';
import { X } from 'lucide-react';
import { galleryImages } from '../data/mock';
import { useLanguage } from '../i18n/LanguageContext';

export const Gallery = () => {
  const { t } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-24 md:py-32 bg-neutral-950 text-neutral-100"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        {/* Header */}
        <div className="max-w-3xl mb-20">
          <div className="flex items-center gap-4 mb-8">
            <span className="divider-line"></span>
            <span className="eyebrow text-lilac">{t.gallery.eyebrow}</span>
          </div>
          <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-neutral-100 tracking-tight mb-8">
            {t.gallery.title}
          </h2>
          <p className="eyebrow text-sage mb-10">{t.gallery.subtitle}</p>
          <div className="space-y-5 max-w-2xl">
            <p className="text-lg md:text-xl text-neutral-400 leading-relaxed font-light">
              {t.gallery.p1}
            </p>
            <p className="text-base md:text-lg text-neutral-500 leading-relaxed font-light">
              {t.gallery.p2}
            </p>
          </div>
        </div>

        {/* Single elegant gallery grid — 2 cols on desktop, 1 on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {galleryImages.map((image, index) => (
            <button
              key={image.id}
              onClick={() => setSelectedImage(image)}
              data-testid={`gallery-item-${image.id}`}
              className="group relative overflow-hidden rounded-2xl aspect-[4/5]"
              style={{ animation: `fadeIn 0.6s ease-out ${index * 0.06}s both` }}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-neutral-950/15 group-hover:bg-neutral-950/0 transition-colors duration-500"></div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-50 bg-neutral-950/95 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            data-testid="lightbox-close"
            className="absolute top-6 right-6 text-neutral-300 hover:text-lilac transition-colors"
            onClick={() => setSelectedImage(null)}
            aria-label="Close"
          >
            <X size={28} strokeWidth={1} />
          </button>
          <img
            src={selectedImage.url}
            alt={selectedImage.alt}
            className="max-w-full max-h-full object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};
